import React, { useState, useEffect, useRef, useContext } from "react";

import * as Yup from "yup";
import { Formik, FieldArray, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CircularProgress from "@material-ui/core/CircularProgress";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Divider from "@material-ui/core/Divider";

import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	dialogPaper: {
		width: 600,
		maxWidth: "95vw",
	},
	dialogTitle: {
		padding: theme.spacing(2, 3),
	},
	dialogContent: {
		padding: theme.spacing(1.5, 3),
	},
	dialogActions: {
		padding: theme.spacing(1.5, 3, 2),
	},
	mainInfoGrid: {
		display: "grid",
		gridTemplateColumns: "1fr 1fr",
		columnGap: theme.spacing(1),
		rowGap: theme.spacing(0.5),
		alignItems: "start",
		[theme.breakpoints.down("xs")]: {
			gridTemplateColumns: "1fr",
		},
	},
	fullRow: {
		gridColumn: "1 / -1",
	},
	textField: {
		width: "100%",
	},
	sectionTitle: {
		marginBottom: theme.spacing(0.5),
		marginTop: theme.spacing(1),
	},

	extraAttr: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},

	helperText: {
		whiteSpace: "normal",
		wordBreak: "break-word",
		maxWidth: "100%",
		minHeight: 18,
	},
}));

const ContactSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, i18n.t("validation.tooShort"))
		.max(50, i18n.t("validation.tooLong"))
		.required(i18n.t("validation.required")),
	number: Yup.string()
		.required(i18n.t("validation.required"))
		.matches(/^\d+$/, i18n.t("validation.onlyNumbers"))
		.min(11, i18n.t("backendErrors.ERR_CONTACT_NUMBER_MISSING_PREFIX"))
		.max(50, i18n.t("validation.tooLong")),
	email: Yup.string().email(i18n.t("validation.invalidEmail")),
});

const ContactModal = ({ open, onClose, contactId, initialValues, onSave, saveToHubSpot, cluster, ownerName: ownerNameProp }) => {
	const classes = useStyles();
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);

	// HubSpot property options (loaded when saveToHubSpot=true)
	const [hubspotProperties, setHubspotProperties] = useState({ leadStatuses: [], tipologie: [] });

	useEffect(() => {
		if (!open || !saveToHubSpot) return;
		api.get("/hubspot/properties")
			.then(({ data }) => setHubspotProperties(data))
			.catch(() => {});
	}, [open, saveToHubSpot]);

	// Prefer assigned user passed from parent (ticket owner); fall back to logged-in user
	const ownerName = (ownerNameProp || (user ? `${user.name || ""}`.trim() : ""));

	const initialState = {
		name: "",
		lastName: "",
		number: "",
		email: "",
		description: "",
		age: "",
		country: "",
		leadStatus: "",
		tipologia: "",
	};

	const [contact, setContact] = useState(initialState);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		const fetchContact = async () => {
			if (initialValues && !contactId) {
				setContact(prevState => {
					return { ...prevState, ...initialValues };
				});
			}

			if (!contactId) return;

			try {
				const { data } = await api.get(`/contacts/${contactId}`);
				if (isMounted.current) {
					// Merge initialValues on top of fetched data so pre-filled fields (e.g. description) take priority
					setContact({ ...initialState, ...data, ...(initialValues || {}) });
				}
			} catch (err) {
				toastError(err);
			}
		};

		fetchContact();
	}, [contactId, open, initialValues]);

	const handleClose = () => {
		onClose();
		setContact(initialState);
	};

	const handleSaveContact = async values => {
		try {
			let savedContact;
			if (contactId) {
				const { data } = await api.put(`/contacts/${contactId}`, values);
				savedContact = data;
				handleClose();
			} else {
				const { data } = await api.post("/contacts", values);
				savedContact = data;
				if (onSave) {
					onSave(data);
				}
				handleClose();
			}
			toast.success(i18n.t("contactModal.success"));

			if (saveToHubSpot && savedContact?.id) {
				try {
					await api.post(`/hubspot/sync/${savedContact.id}`, {
						cluster: cluster || "",
						ownerName
					});
					toast.success(i18n.t("contactModal.hubspotSuccess"));
				} catch (err) {
					toastError(err);
				}
			}
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<div className={classes.root}>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth={false}
				scroll="paper"
				PaperProps={{ className: classes.dialogPaper }}
			>
				<DialogTitle id="form-dialog-title" className={classes.dialogTitle}>
					{contactId
						? `${i18n.t("contactModal.title.edit")}`
						: `${i18n.t("contactModal.title.add")}`}
					{saveToHubSpot && (
						<Typography variant="caption" display="block" style={{ color: "#ff7a59", fontWeight: 600 }}>
							{i18n.t("contactModal.hubspotFlow")}
						</Typography>
					)}
				</DialogTitle>
				<Formik
					initialValues={contact}
					enableReinitialize={true}
					validationSchema={ContactSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveContact(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, errors, touched, isSubmitting }) => (
						<Form>
							<DialogContent dividers className={classes.dialogContent}>
								<Typography
									variant="subtitle1"
									className={classes.sectionTitle}
								>
									{i18n.t("contactModal.form.mainInfo")}
								</Typography>
								<div className={classes.mainInfoGrid}>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.name")}
										name="name"
										autoFocus
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										variant="outlined"
										margin="dense"
										className={classes.textField}
									/>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.lastName")}
										name="lastName"
										variant="outlined"
										margin="dense"
										className={classes.textField}
									/>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.email")}
										name="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										placeholder="Email address"
										fullWidth
										margin="dense"
										variant="outlined"
										className={classes.textField}
									/>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.age")}
										name="age"
										variant="outlined"
										margin="dense"
										className={classes.textField}
									/>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.number")}
										name="number"
										error={touched.number && Boolean(errors.number)}
										helperText={
											touched.number ? errors.number || " " : " "
										}
										FormHelperTextProps={{
											className: classes.helperText,
										}}
										placeholder="5513912344321"
										fullWidth
										margin="dense"
										variant="outlined"
										className={`${classes.textField} ${classes.fullRow}`}
									/>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.country")}
										name="country"
										variant="outlined"
										margin="dense"
										className={`${classes.textField} ${classes.fullRow}`}
									/>
									<Field
										as={TextField}
										label={i18n.t("contactModal.form.description")}
										name="description"
										multiline
										minRows={3}
										variant="outlined"
										margin="dense"
										className={`${classes.textField} ${classes.fullRow}`}
										placeholder={i18n.t("contactModal.form.descriptionPlaceholder")}
									/>
								</div>

								{saveToHubSpot && (
									<>
										<Divider style={{ margin: "12px 0 8px" }} />
										<Typography variant="subtitle1" className={classes.sectionTitle}>
											{i18n.t("contactModal.form.hubspotSection")}
										</Typography>
										<div className={classes.mainInfoGrid}>
											<FormControl
												variant="outlined"
												margin="dense"
												className={classes.textField}
											>
												<InputLabel>{i18n.t("contactModal.form.leadStatus")}</InputLabel>
												<Field name="leadStatus">
													{({ field }) => (
														<Select {...field} label={i18n.t("contactModal.form.leadStatus")}>
															<MenuItem value=""><em>—</em></MenuItem>
															{hubspotProperties.leadStatuses.map(opt => (
																<MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
															))}
														</Select>
													)}
												</Field>
											</FormControl>
											<FormControl
												variant="outlined"
												margin="dense"
												className={classes.textField}
											>
												<InputLabel>{i18n.t("contactModal.form.tipologia")}</InputLabel>
												<Field name="tipologia">
													{({ field }) => (
														<Select {...field} label={i18n.t("contactModal.form.tipologia")}>
															<MenuItem value=""><em>—</em></MenuItem>
															{hubspotProperties.tipologie.map(opt => (
																<MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
															))}
														</Select>
													)}
												</Field>
											</FormControl>
											<TextField
												label={i18n.t("contactModal.form.cluster")}
												value={cluster || ""}
												variant="outlined"
												margin="dense"
												className={classes.textField}
												InputProps={{ readOnly: true }}
											/>
											<TextField
												label={i18n.t("contactModal.form.owner")}
												value={ownerName}
												variant="outlined"
												margin="dense"
												className={classes.textField}
												InputProps={{ readOnly: true }}
											/>
										</div>
									</>
								)}

								<Typography
									className={classes.sectionTitle}
									variant="subtitle1"
								>
									{i18n.t("contactModal.form.extraInfo")}
								</Typography>

								<FieldArray name="extraInfo">
									{({ push, remove }) => (
										<>
											{values.extraInfo &&
												values.extraInfo.length > 0 &&
												values.extraInfo.map((info, index) => (
													<div
														className={classes.extraAttr}
														key={`${index}-info`}
													>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraName")}
															name={`extraInfo[${index}].name`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<Field
															as={TextField}
															label={i18n.t("contactModal.form.extraValue")}
															name={`extraInfo[${index}].value`}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<IconButton
															size="small"
															onClick={() => remove(index)}
														>
															<DeleteOutlineIcon />
														</IconButton>
													</div>
												))}
											<div className={classes.extraAttr}>
												<Button
													style={{ flex: 1, marginTop: 8 }}
													variant="outlined"
													color="primary"
													onClick={() => push({ name: "", value: "" })}
												>
													{`+ ${i18n.t("contactModal.buttons.addExtraInfo")}`}
												</Button>
											</div>
										</>
									)}
								</FieldArray>
							</DialogContent>
							<DialogActions className={classes.dialogActions}>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("contactModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{saveToHubSpot ? (
										<>
											<CloudUploadIcon style={{ marginRight: 6, fontSize: 18 }} />
											{i18n.t("contactModal.buttons.saveToHubSpot")}
										</>
									) : contactId
										? `${i18n.t("contactModal.buttons.okEdit")}`
										: `${i18n.t("contactModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default ContactModal;
